import type {
  LinksFunction,
  LoaderFunctionArgs,
  MetaFunction,
} from "react-router";
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
} from "react-router";

import { sessionStorage } from "#shared/Session.server";
import { getUserById } from "#shared/User.server";

import mainStylesheetHref from "./styles/index.css?url";
import tailwindStylesheetHref from "./styles/tailwind.css?url";

export const links: LinksFunction = () => {
  return [
    { rel: "stylesheet", href: tailwindStylesheetHref },
    { rel: "stylesheet", href: mainStylesheetHref },
  ];
};

export const meta: MetaFunction = () => [
  {
    charSet: "utf-8",
  },
  {
    title: "Pepettes",
  },
  {
    name: "viewport",
    content: "width=device-width,initial-scale=1",
  },
];

export async function loader({ request }: LoaderFunctionArgs) {
  const session = await sessionStorage.getSession(
    request.headers.get("cookie"),
  );

  const userIdentity = session.get("user");
  if (!userIdentity) {
    return {
      user: null,
    };
  }

  return {
    user: await getUserById(userIdentity.id),
  };
}

export default function App() {
  return (
    <html lang="en" data-theme="bumblebee">
      <head>
        <Meta />
        <Links />
      </head>
      <body className="bg-base-100">
        <Outlet />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();

  if (isRouteErrorResponse(error)) {
    return (
      <section className="bg-base-200 min-h-screen">
        <div className="mx-auto max-w-(--breakpoint-xl) px-4 py-8 lg:px-6 lg:py-16">
          <div className="mx-auto max-w-(--breakpoint-sm) text-center">
            <h1 className="text-primary mb-4 text-7xl font-extrabold tracking-tight lg:text-9xl">
              {error.status}
            </h1>
            <p className="mb-4 text-3xl font-bold tracking-tight text-gray-900 md:text-4xl">
              {error.statusText}.
            </p>
            <p className="mb-4 text-lg font-light text-gray-500">
              {error.status === 404
                ? "Sorry, we can't find that page."
                : "We are already working to solve the problem."}
            </p>
          </div>
        </div>
      </section>
    );
  }

  // Don't forget to typecheck with your own logic.
  // Any value can be thrown, not just errors!
  let errorMessage = "Unknown error";
  if (error instanceof Error) {
    errorMessage = error.message;
  }

  return (
    <div>
      <h1>Uh oh ...</h1>
      <p>Something went wrong.</p>
      <pre>{errorMessage}</pre>
    </div>
  );
}
